import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  MessageSquare,
  Plus,
  Filter,
  Flame,
  Star,
  Clock,
  Eye,
  MessageCircle,
  ThumbsUp,
  ChevronRight,
  Loader2,
  Search,
  Zap,
  Fish,
  Shield,
  AlertCircle,
  User
} from 'lucide-react'
import SEO from '../components/SEO'
import { useAuth } from '../contexts/AuthContext'
import { goToLogin } from '../utils/authRedirect'
import api from '../utils/api'
import useSiteLanguage from '../hooks/useSiteLanguage'

function Forum() {
  const [categories, setCategories] = useState([])
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('new')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const { user, getLevelName } = useAuth()
  const navigate = useNavigate()
  const { lang } = useSiteLanguage()

  const text = {
    zh: {
      seoTitle: '讨论区',
      seoDescription: 'DClaw社区讨论区，与其他用户交流经验、分享技能、解决问题。参与热门话题讨论，获取AI助手使用技巧。',
      title: '讨论区',
      subtitle: '与其他用户交流经验、分享技能、解决问题',
      newPost: '发布帖子',
      searchPlaceholder: '搜索帖子标题或内容...',
      search: '搜索',
      all: '全部',
      myPosts: '我的帖子',
      newest: '最新',
      hottest: '热门',
      featured: '精华',
      noPosts: '暂无帖子',
      firstPostAll: '发布第一个帖子 →',
      firstPostCategory: (name) => `发布第一个${name}帖子 →`,
      pinned: '置顶',
      essence: '精华',
      justNow: '刚刚',
      minutesAgo: (value) => `${value}分钟前`,
      hoursAgo: (value) => `${value}小时前`,
      daysAgo: (value) => `${value}天前`,
      emptySearchCategory: '暂无结果'
    },
    en: {
      seoTitle: 'Forum',
      seoDescription: 'Join the DClaw community forum to discuss skills, share experience, and solve problems together.',
      title: 'Forum',
      subtitle: 'Discuss ideas, share skills, and solve problems with other users',
      newPost: 'New Post',
      searchPlaceholder: 'Search post titles or content...',
      search: 'Search',
      all: 'All',
      myPosts: 'My Posts',
      newest: 'Newest',
      hottest: 'Hot',
      featured: 'Featured',
      noPosts: 'No posts yet',
      firstPostAll: 'Create the first post →',
      firstPostCategory: (name) => `Create the first ${name} post →`,
      pinned: 'Pinned',
      essence: 'Featured',
      justNow: 'Just now',
      minutesAgo: (value) => `${value} min ago`,
      hoursAgo: (value) => `${value} hr ago`,
      daysAgo: (value) => `${value} day(s) ago`,
      emptySearchCategory: 'No results'
    },
    fr: {
      seoTitle: 'Forum',
      seoDescription: 'Participez au forum DClaw pour echanger des idees, partager des competences et resoudre des problemes.',
      title: 'Forum',
      subtitle: 'Echangez avec les autres utilisateurs, partagez des competences et resolvez des problemes',
      newPost: 'Nouvelle publication',
      searchPlaceholder: 'Rechercher dans les titres ou le contenu...',
      search: 'Rechercher',
      all: 'Tout',
      myPosts: 'Mes publications',
      newest: 'Recent',
      hottest: 'Populaire',
      featured: 'A la une',
      noPosts: 'Aucune publication',
      firstPostAll: 'Creer la premiere publication →',
      firstPostCategory: (name) => `Creer la premiere publication ${name} →`,
      pinned: 'Epingle',
      essence: 'A la une',
      justNow: 'A l’instant',
      minutesAgo: (value) => `Il y a ${value} min`,
      hoursAgo: (value) => `Il y a ${value} h`,
      daysAgo: (value) => `Il y a ${value} j`,
      emptySearchCategory: 'Aucun resultat'
    },
    de: {
      seoTitle: 'Forum',
      seoDescription: 'Treten Sie dem DClaw-Forum bei, um Erfahrungen auszutauschen, Funktionen zu teilen und Probleme zu loesen.',
      title: 'Forum',
      subtitle: 'Tauschen Sie Erfahrungen aus, teilen Sie Funktionen und loesen Sie Probleme mit anderen Nutzern',
      newPost: 'Neuer Beitrag',
      searchPlaceholder: 'Nach Titeln oder Inhalten suchen...',
      search: 'Suchen',
      all: 'Alle',
      myPosts: 'Meine Beiträge',
      newest: 'Neueste',
      hottest: 'Beliebt',
      featured: 'Empfohlen',
      noPosts: 'Noch keine Beiträge',
      firstPostAll: 'Ersten Beitrag erstellen →',
      firstPostCategory: (name) => `Ersten ${name}-Beitrag erstellen →`,
      pinned: 'Angeheftet',
      essence: 'Empfohlen',
      justNow: 'Gerade eben',
      minutesAgo: (value) => `Vor ${value} Min`,
      hoursAgo: (value) => `Vor ${value} Std`,
      daysAgo: (value) => `Vor ${value} Tag(en)`,
      emptySearchCategory: 'Keine Ergebnisse'
    }
  }[lang]

  useEffect(() => {
    fetchCategories()
    fetchPosts()
  }, [selectedCategory, sortBy, searchKeyword])

  // 处理发帖按钮点击 - 未登录时跳转登录页
  const handleNewPost = () => {
    console.log('[Forum] handleNewPost called:', { user: !!user })

    if (!user) {
      console.log('[Forum] User not logged in, redirecting to login...')
      goToLogin('/login')
      return
    }
    const categoryId = selectedCategory !== 'all' && selectedCategory !== 'my'
      ? categories.find(c => c.slug === selectedCategory)?.id || ''
      : ''
    navigate(categoryId ? `/forum/new?categoryId=${categoryId}` : '/forum/new')
  }

  const fetchCategories = async () => {
    try {
      const params = new URLSearchParams()
      // 如果有搜索关键词，传递过去计算各板块数量
      if (searchKeyword.trim()) {
        params.append('keyword', searchKeyword.trim())
      }
      const response = await api.get(`/forum/categories?${params.toString()}`)
      console.log('Loaded categories:', response.data.categories)
      if (response.data.success) {
        setCategories(response.data.categories)
      }
    } catch (error) {
      console.error('获取板块失败:', error)
    }
  }

  const fetchPosts = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (selectedCategory !== 'all' && selectedCategory !== 'my') {
        params.append('category', selectedCategory)
      }
      // 如果是"我的帖子"，添加 user_id 参数
      if (selectedCategory === 'my' && user) {
        params.append('user_id', user.id)
      }
      params.append('sort', sortBy)
      
      // 添加搜索关键词
      if (searchKeyword.trim()) {
        params.append('keyword', searchKeyword.trim())
      }
      
      const response = await api.get(`/forum/posts?${params.toString()}`)
      if (response.data.success) {
        // 如果是"我的帖子"，前端再过滤一次确保只显示当前用户的帖子
        let posts = response.data.posts
        if (selectedCategory === 'my' && user) {
          posts = posts.filter(post => post.user_id === user.id)
        }
        setPosts(posts)
      }
    } catch (error) {
      console.error('获取帖子失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setIsSearching(true)
    fetchPosts()
  }

  const clearSearch = () => {
    setSearchKeyword('')
    setIsSearching(false)
    fetchPosts()
  }

  const getCategoryIcon = (slug) => {
    switch (slug) {
      case 'skill': return <Zap className="w-5 h-5" />
      case 'shrimp': return <Fish className="w-5 h-5" />
      case 'guide': return <Shield className="w-5 h-5" />
      case 'complaint': return <AlertCircle className="w-5 h-5" />
      default: return <MessageSquare className="w-5 h-5" />
    }
  }

  const getCategoryDisplayName = (category) => {
    if (!category) return text.emptySearchCategory
    if (lang === 'zh') return category.name
    const map = {
      skill: { en: 'Skills', fr: 'Compétences', de: 'Funktionen' },
      shrimp: { en: 'Community', fr: 'Communauté', de: 'Community' },
      guide: { en: 'Guides', fr: 'Guides', de: 'Leitfäden' },
      complaint: { en: 'Feedback', fr: 'Feedback', de: 'Feedback' }
    }
    return map[category.slug]?.[lang] || category.name
  }

  const getLocalizedLevelName = (level) => {
    if (lang === 'zh') return getLevelName(level)
    const levelMap = {
      1: { en: 'New', fr: 'Nouveau', de: 'Neu' },
      2: { en: 'Active', fr: 'Actif', de: 'Aktiv' },
      3: { en: 'Contributor', fr: 'Contributeur', de: 'Beitrag' },
      4: { en: 'Expert', fr: 'Expert', de: 'Experte' },
      5: { en: 'Leader', fr: 'Leader', de: 'Leiter' },
      6: { en: 'Master', fr: 'Maître', de: 'Meister' }
    }
    return levelMap[level]?.[lang] || ''
  }

  const getCategoryColor = (slug) => {
    switch (slug) {
      case 'skill': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
      case 'shrimp': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      case 'guide': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
      case 'complaint': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  const getLevelColor = (level) => {
    const colors = {
      1: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      2: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      3: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      4: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      5: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      6: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    }
    return colors[level] || colors[1]
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    
    if (diff < 60000) return text.justNow
    if (diff < 3600000) return text.minutesAgo(Math.floor(diff / 60000))
    if (diff < 86400000) return text.hoursAgo(Math.floor(diff / 3600000))
    if (diff < 604800000) return text.daysAgo(Math.floor(diff / 86400000))
    return date.toLocaleDateString(lang === 'zh' ? 'zh-CN' : lang)
  }

  return (
    <div className="animate-in">
      <SEO
        title={text.seoTitle}
        description={text.seoDescription}
        keywords="小龙虾， openclaw，一键安装"
      />
      {/* Header */}
      <section className="bg-gradient-to-b from-dclaw-50 to-white dark:from-gray-900 dark:to-gray-900 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {text.title}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {text.subtitle}
              </p>
            </div>
            <button
              onClick={handleNewPost}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 text-white rounded-xl font-medium transition-colors min-h-[44px]"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">{text.newPost}</span>
            </button>
          </div>

          {/* 搜索框 */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder={text.searchPlaceholder}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dclaw-500 focus:border-transparent min-h-[44px]"
              />
              {searchKeyword && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 w-6 h-6 flex items-center justify-center"
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-dclaw-600 hover:bg-dclaw-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2 min-h-[44px] text-sm sm:text-base"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">{text.search}</span>
            </button>
          </form>
        </div>
      </section>

      {/* Categories */}
      <section className="py-4 sm:py-6 border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-nowrap sm:flex-wrap gap-2 sm:gap-3 min-w-max sm:min-w-0">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors min-h-[44px] whitespace-nowrap ${
                selectedCategory === 'all'
                  ? 'bg-dclaw-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {text.all}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors min-h-[44px] whitespace-nowrap ${
                  selectedCategory === cat.slug
                    ? 'bg-dclaw-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {getCategoryIcon(cat.slug)}
                <span>{getCategoryDisplayName(cat)}</span>
                <span className="ml-0.5 sm:ml-1 text-xs opacity-70">({cat.post_count})</span>
              </button>
            ))}
            {/* 我的帖子 - 仅登录用户可见 */}
            {user && (
              <button
                onClick={() => setSelectedCategory('my')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors min-h-[44px] whitespace-nowrap ${
                  selectedCategory === 'my'
                    ? 'bg-dclaw-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{text.myPosts}</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sort */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setSortBy('new')}
                className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm transition-colors min-h-[36px] ${
                  sortBy === 'new'
                    ? 'bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {text.newest}
              </button>
              <button
                onClick={() => setSortBy('hot')}
                className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm transition-colors min-h-[36px] ${
                  sortBy === 'hot'
                    ? 'bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {text.hottest}
              </button>
              <button
                onClick={() => setSortBy('essence')}
                className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm transition-colors min-h-[36px] ${
                  sortBy === 'essence'
                    ? 'bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {text.featured}
              </button>
            </div>
          </div>

          {/* Posts List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-dclaw-600" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 mb-4">{text.noPosts}</p>
              <button
                onClick={handleNewPost}
                className="text-dclaw-600 hover:text-dclaw-700 font-medium"
              >
                {selectedCategory === 'all' || selectedCategory === 'my'
                  ? text.firstPostAll
                  : text.firstPostCategory(getCategoryDisplayName(categories.find(c => c.slug === selectedCategory)))}
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/forum/post/${post.id}`}
                  className="block bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 hover:border-dclaw-300 dark:hover:border-dclaw-700 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* User Avatar */}
                    <div className="flex-shrink-0">
                      {post.avatar ? (
                        <img
                          src={post.avatar}
                          alt={post.nickname}
                          className="w-9 h-9 sm:w-10 sm:h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-dclaw-400 to-dclaw-600 flex items-center justify-center text-white font-medium text-sm sm:text-base">
                          {post.nickname?.[0] || '?'}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Title & Tags */}
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        {post.is_pinned === 1 && (
                          <span className="px-1.5 sm:px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs rounded-full">
                            {text.pinned}
                          </span>
                        )}
                        {post.is_essence === 1 && (
                          <span className="px-1.5 sm:px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs rounded-full">
                            {text.essence}
                          </span>
                        )}
                        <span className={`px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${getCategoryColor(post.category_slug)}`}>
                          {getCategoryDisplayName({ slug: post.category_slug, name: post.category_name })}
                        </span>
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate text-sm sm:text-base w-full sm:w-auto mt-0.5 sm:mt-0">
                          {post.title}
                        </h3>
                      </div>

                      {/* Content Preview */}
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2 sm:mb-3">
                        {post.content}
                      </p>

                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {/* Level Badge */}
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${getLevelColor(post.level)}`}>
                          Lv{post.level} {getLocalizedLevelName(post.level)}
                        </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {post.nickname}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(post.created_at)}
                        </span>
                        <div className="flex items-center gap-3 sm:gap-4">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {post.reply_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3" />
                            {post.likes}
                          </span>
                        </div>
                      </div>
                    </div>

                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0 hidden sm:block" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Forum
